import { OUTCOME, lockingOutcome, reconcileWithList, runGeneration } from './billGeneration'

/**
 * Spec 158 §3.4 (review fix) — the Generate Bills run lives HERE, not in the page.
 *
 * The first build kept the run, its results and the locks in the page's own state. A
 * navigation inside the app during a run unmounted the page: the in-flight matter's
 * answer landed on nothing, the locks were gone, and on the way back every matter was
 * selectable again — including the one Centerbase may have been billing at that moment.
 * So:
 *  - the run, its progress and its results live in this module, which outlives the page;
 *  - so do the locks, per tenant, written through to sessionStorage where the browser
 *    allows it, so that they also survive a reload of the tab. A matter is locked as IN
 *    FLIGHT before its request is sent, and one still in flight when the tab reloads is
 *    read back as UNKNOWN: its answer was never seen;
 *  - one run at a time across mounts: a page that mounts while a run is in flight shows
 *    that run and cannot start another;
 *  - while a run is in flight the browser asks before the tab is closed or reloaded —
 *    this module's own `beforeunload`, so it holds after the page has been left too.
 * The page blocks in-app navigation itself (react-router's `useBlocker`).
 *
 * sessionStorage only extends the in-memory locks across a reload; when storage is
 * unavailable the locks still hold for every in-app navigation.
 */

export const IN_FLIGHT = 'in-flight'
/** How long the re-read after a run may take before the page stops waiting for it. */
export const REREAD_TIMEOUT_MS = 30000
const STORAGE_PREFIX = 'cb2.generateBills.locks.'

const IDLE = Object.freeze({ phase: 'idle', progress: null, results: [], stopRequested: false, reread: null, runCount: 0, locksVersion: 0 })
let state = IDLE
let currentRun = 0
const listeners = new Set()
const locksByTenant = new Map()

const key = (id) => String(id).toLowerCase()
const emit = () => { for (const l of [...listeners]) l() }
const set = (patch) => { state = { ...state, ...patch }; emit() }

const warnBeforeUnload = (e) => { e.preventDefault(); e.returnValue = '' }

/** useSyncExternalStore's subscribe. */
export const subscribe = (listener) => {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}
/**
 * The run as the page shows it: `phase` ('idle' | 'running' | 'done'), `progress`,
 * `results`, `stopRequested`, `reread` (null | 'reading' | 'done' | 'failed' | 'offline').
 * The same object until something changes.
 */
export const getSnapshot = () => state
export const isRunning = () => state.phase === 'running'

const storageKey = (tenant) => `${STORAGE_PREFIX}${tenant || 'unknown'}`

function loadLocks(tenant) {
  const locks = new Map()
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(storageKey(tenant)) || 'null')
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      for (const [id, outcome] of Object.entries(parsed)) {
        // Still "in flight" in storage means this tab was reloaded before the answer came.
        if (outcome === IN_FLIGHT) locks.set(key(id), OUTCOME.UNKNOWN)
        else if (lockingOutcome(outcome)) locks.set(key(id), outcome)
      }
    }
  } catch {
    // No storage, or nothing readable in it: the locks are this page load's only.
  }
  return locks
}

function saveLocks(tenant, locks) {
  try {
    window.sessionStorage.setItem(storageKey(tenant), JSON.stringify(Object.fromEntries(locks)))
  } catch {
    // Storage unavailable: the in-memory locks still hold until the tab reloads.
  }
}

/**
 * The matters that must not be generated again in this session, for this tenant:
 * matterId (lower case) → 'created' | 'none' | 'unknown' | IN_FLIGHT. A new Map
 * whenever it changes.
 */
export function locksFor(tenant) {
  if (!locksByTenant.has(tenant)) locksByTenant.set(tenant, loadLocks(tenant))
  return locksByTenant.get(tenant)
}

function setLock(tenant, matterId, outcome) {
  const next = new Map(locksFor(tenant))
  if (outcome) next.set(key(matterId), outcome)
  else next.delete(key(matterId))
  locksByTenant.set(tenant, next)
  saveLocks(tenant, next)
  set({ locksVersion: state.locksVersion + 1 })
}

/** Ask the run to stop before its next matter. The one in flight cannot be recalled. */
export function requestStop() {
  if (state.phase === 'running' && !state.stopRequested) set({ stopRequested: true })
}

const withTimeout = (promise, ms) => {
  let timer
  return Promise.race([
    promise,
    new Promise((resolve) => { timer = setTimeout(() => resolve(null), ms) }),
  ]).finally(() => clearTimeout(timer))
}

/**
 * Start a run, unless one is already in flight — then nothing is sent and this returns
 * null. Resolves to the run's results once the list has been read again (or not).
 *
 * @param {{
 *   tenant: string,
 *   matters: Array<{ matterId: string, matterName: string }>,
 *   period: { settingsId: number, startDate: string|null, endDate: string },
 *   issueDate: string,
 *   lastBillBefore: Map<string, string|null>,
 *   generate: (request: object) => Promise<unknown>,
 *   onResult?: (result: object) => void,
 *   isOnline?: () => boolean,
 *   reread?: () => Promise<Array<object>|null>,
 * }} run  `onResult` is told each matter's result after it is recorded here.
 */
export function startRun({ tenant, matters, period, issueDate, lastBillBefore, generate, onResult, isOnline, reread }) {
  if (state.phase === 'running') return null
  currentRun += 1
  const runId = currentRun
  const live = () => runId === currentRun
  set({
    phase: 'running',
    progress: { index: 0, total: matters.length, matterName: matters[0]?.matterName },
    results: [],
    stopRequested: false,
    reread: null,
    runCount: state.runCount + 1,
  })
  window.addEventListener('beforeunload', warnBeforeUnload)

  return (async () => {
    let out = []
    try {
      out = await runGeneration({
        matters,
        period,
        issueDate,
        generate: (request) => {
          // Locked BEFORE it is sent: if the tab dies now, the matter reads back as unknown.
          if (live()) setLock(tenant, request.matterId, IN_FLIGHT)
          return generate(request)
        },
        onStart: (m, i) => { if (live()) set({ progress: { index: i, total: matters.length, matterName: m.matterName } }) },
        onResult: (r) => {
          if (!live()) return
          if (lockingOutcome(r.outcome)) setLock(tenant, r.matterId, r.outcome)
          else if (locksFor(tenant).get(key(r.matterId)) === IN_FLIGHT) setLock(tenant, r.matterId, null)
          set({ results: [...state.results, r] })
          onResult?.(r)
        },
        shouldStop: () => !live() || state.stopRequested,
        isOnline,
      })
    } finally {
      window.removeEventListener('beforeunload', warnBeforeUnload)
      // The run is over now: the page says so at once, not after the list is read again.
      if (live()) set({ phase: 'done', progress: null })
    }
    if (!live()) return out

    // Read the list again: CB's own figures and last bill are the record of what
    // happened. Not when offline — a paused read would never settle — and never for
    // longer than REREAD_TIMEOUT_MS. A failed re-read leaves the results as reported.
    if (!reread) return out
    if (isOnline && !isOnline()) {
      set({ reread: 'offline' })
      return out
    }
    set({ reread: 'reading' })
    let rowsAfter = null
    try {
      rowsAfter = await withTimeout(reread(), REREAD_TIMEOUT_MS)
    } catch {
      rowsAfter = null
    }
    if (!live()) return out
    const reconciled = reconcileWithList(out, lastBillBefore, rowsAfter)
    set({ results: reconciled, reread: Array.isArray(rowsAfter) ? 'done' : 'failed' })
    return reconciled
  })()
}

/** Tests only: forget every run and lock, as a fresh page load would. */
export function _resetForTests() {
  currentRun += 1
  window.removeEventListener('beforeunload', warnBeforeUnload)
  locksByTenant.clear()
  state = IDLE
  emit()
}
